/**
 * Locations dashboard
 *
 * - Header with actions
 * - Stat tiles: active campuses, weekly services, venues, campuses with a primary leader
 * - Campus cards (address, weekly services, venues, leaders, gaps) + "Needs attention"
 *
 * Data: GET api/locations/view (the shared ['locations', 'list-view'] key) plus the archived count.
 */

import { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  IconAlertTriangle,
  IconBuildingChurch,
  IconCalendarWeek,
  IconChevronRight,
  IconCircleCheck,
  IconClock,
  IconDoor,
  IconMapPin,
  IconPlus,
  IconRefresh,
  IconShare,
  IconUserStar,
  IconUsers,
} from '@tabler/icons-react';
import { LayoutContainer } from '@/components/ui/layout';
import { Box, Button, Card, Grid, Group, SimpleGrid, Skeleton, Stack, Text, Tooltip } from '@/components/ui';
import { EmptyState } from '@/components/ui/data-display';
import { FadeIn } from '@/components/animations';
import { useLocationService, type LocationModelView } from '@actschurch/ccms-sdk';
import { useLocationsPermissions } from '@/hooks/locations/useLocationsPermissions';
import {
  CAMPUSES_PATH,
  ColorSwatch,
  ErrorNotice,
  LocationStatusBadge,
  NEW_LOCATION_PATH,
  PageHeader,
  SectionCard,
  StatTile,
  StatTilesSkeleton,
  Tag,
  byName,
  cityCountry,
  colorOrNull,
  formatNumber,
  isOnlineVenue,
  leaderState,
  locationIssues,
  locationPath,
  locationsKeys,
  plural,
  useLeaderNames,
  weeklySummary,
  type LocationIssue,
} from '../components';

function CampusCard({ view, primaryName, onOpen }: { view: LocationModelView; primaryName: string | null; onOpen: () => void }) {
  const issues = locationIssues(view);
  const leaders = leaderState(view.leaders);
  const summary = weeklySummary(view.serviceTimes ?? []);
  const place = cityCountry(view.address);
  const color = colorOrNull(view.color);
  return (
    <Card withBorder radius="md" padding="md" shadow="sm" onClick={onOpen} style={{ cursor: 'pointer', position: 'relative', overflow: 'hidden', opacity: view.isActive ? 1 : 0.8 }}>
      <Box style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, backgroundColor: color ?? 'var(--mantine-color-default-border)' }} />
      <Stack gap={8} pl={4}>
        <Group justify="space-between" wrap="nowrap" align="flex-start">
          <Group gap={8} wrap="nowrap" style={{ minWidth: 0 }}>
            <ColorSwatch color={view.color} />
            <Text fw={700} lineClamp={1}>
              {view.name}
            </Text>
          </Group>
          <LocationStatusBadge location={view} size="xs" />
        </Group>
        <Group gap={6} wrap="nowrap" align="flex-start">
          <IconMapPin size={14} style={{ marginTop: 3, flexShrink: 0, color: 'var(--mantine-color-dimmed)' }} />
          <Text size="sm" c={place ? undefined : 'dimmed'} lineClamp={1}>
            {place || (view.hasAddress ? 'Address has no city or country' : 'No address yet')}
          </Text>
        </Group>
        <Group gap={6} wrap="nowrap" align="flex-start">
          <IconClock size={14} style={{ marginTop: 3, flexShrink: 0, color: 'var(--mantine-color-dimmed)' }} />
          <Text size="sm" c={summary ? undefined : 'dimmed'} lineClamp={2}>
            {summary || 'No active service times'}
          </Text>
        </Group>
        <Group gap={6} wrap="nowrap" align="flex-start">
          <IconUserStar size={14} style={{ marginTop: 3, flexShrink: 0, color: 'var(--mantine-color-dimmed)' }} />
          <Text size="sm" c={leaders.ok ? undefined : 'dimmed'} lineClamp={1}>
            {leaders.ok ? primaryName : leaders.noLeaders ? 'No leaders' : leaders.noPrimary ? 'No primary leader' : `${leaders.primaryIds.length} leaders marked primary`}
          </Text>
        </Group>
        <Group gap={6}>
          <Tag icon={<IconDoor size={11} />}>{plural(view.totalVenues ?? (view.venues ?? []).length, 'venue')}</Tag>
          <Tag icon={<IconUsers size={11} />}>{plural(leaders.count, 'leader')}</Tag>
          {(view.socialMedias ?? []).length > 0 && <Tag icon={<IconShare size={11} />}>{plural((view.socialMedias ?? []).length, 'account')}</Tag>}
          {view.isActive &&
            issues.map((i) => (
              <Tag key={i.key} color={i.severity === 'high' ? 'red' : 'orange'}>
                {i.label}
              </Tag>
            ))}
        </Group>
      </Stack>
    </Card>
  );
}

export default function LocationsDashboard() {
  const navigate = useNavigate();
  const perms = useLocationsPermissions();
  const locationService = useLocationService();

  const views = useQuery({ queryKey: locationsKeys.listView, queryFn: () => locationService.getAllViews(), staleTime: 60_000 });
  const archived = useQuery({ queryKey: locationsKeys.archived, queryFn: () => locationService.getArchived(), staleTime: 60_000, retry: false });

  const rows = useMemo(() => (views.data ?? []).slice().sort((a, b) => Number(b.isActive) - Number(a.isActive) || byName(a, b)), [views.data]);
  const active = rows.filter((r) => r.isActive);

  // Leader names are resolved server-side (no Member.Read needed).
  const names = useLeaderNames(perms.canViewLeaders);

  const stats = useMemo(() => {
    const services = active.flatMap((r) => (r.serviceTimes ?? []).filter((s) => s.isActive && !s.isDeleted));
    const venues = active.flatMap((r) => (r.venues ?? []).filter((v) => v.isActive && !v.isDeleted));
    const online = venues.filter(isOnlineVenue).length;
    const withPrimary = active.filter((r) => leaderState(r.leaders).ok).length;
    const campusesWithServices = new Set(services.map((s) => s.locationID)).size;
    return { services: services.length, campusesWithServices, venues: venues.length, online, withPrimary };
  }, [active]);

  const attention = useMemo(() => {
    const list: { view: LocationModelView; issue: LocationIssue }[] = [];
    for (const view of active) for (const issue of locationIssues(view)) list.push({ view, issue });
    return list.sort((a, b) => (a.issue.severity === b.issue.severity ? byName(a.view, b.view) : a.issue.severity === 'high' ? -1 : 1));
  }, [active]);

  const inactiveCount = rows.length - active.length;
  const archivedCount = archived.data?.length ?? 0;

  const header = (
    <PageHeader
      title="Locations"
      description="Campuses with their addresses, service times, venues and leaders. Members, Meetings, Carpool, Events and other modules read them from here."
      actions={
        <>
          <Button variant="default" leftSection={<IconRefresh size={16} />} onClick={() => views.refetch()} loading={views.isFetching && !views.isLoading}>
            Refresh
          </Button>
          {perms.canCreate && (
            <Button leftSection={<IconPlus size={16} />} component={Link} to={NEW_LOCATION_PATH}>
              New location
            </Button>
          )}
        </>
      }
    />
  );

  if (views.isLoading) {
    return (
      <LayoutContainer>
        <FadeIn>
          <Stack gap="lg">
            {header}
            <StatTilesSkeleton count={4} />
            <Grid>
              <Grid.Col span={{ base: 12, lg: 8 }}>
                <SimpleGrid cols={{ base: 1, sm: 2 }}>
                  {[1, 2, 3, 4].map((i) => (
                    <Skeleton key={i} height={170} radius="md" />
                  ))}
                </SimpleGrid>
              </Grid.Col>
              <Grid.Col span={{ base: 12, lg: 4 }}>
                <Skeleton height={360} radius="md" />
              </Grid.Col>
            </Grid>
          </Stack>
        </FadeIn>
      </LayoutContainer>
    );
  }

  return (
    <LayoutContainer>
      <FadeIn>
        <Stack gap="lg">
          {header}
          <ErrorNotice error={views.error} title="Could not load the locations" onRetry={() => views.refetch()} />

          <SimpleGrid cols={{ base: 1, xs: 2, md: 4 }}>
            <StatTile
              icon={<IconBuildingChurch size={22} />}
              color="blue"
              label="Active campuses"
              value={formatNumber(active.length)}
              hint={`${formatNumber(inactiveCount)} inactive · ${formatNumber(archivedCount)} archived`}
              onClick={() => navigate(CAMPUSES_PATH)}
            />
            <StatTile
              icon={<IconCalendarWeek size={22} />}
              color="grape"
              label="Weekly services"
              value={formatNumber(stats.services)}
              hint={`at ${plural(stats.campusesWithServices, 'campus', 'campuses')}`}
              onClick={perms.canViewServiceTimes ? () => navigate('/app/locations/service-schedule') : undefined}
            />
            <StatTile
              icon={<IconDoor size={22} />}
              color="teal"
              label="Active venues"
              value={formatNumber(stats.venues)}
              hint={`${formatNumber(stats.online)} online`}
              onClick={perms.canViewVenues ? () => navigate('/app/locations/venues') : undefined}
            />
            <StatTile
              icon={<IconUserStar size={22} />}
              color={stats.withPrimary < active.length ? 'orange' : 'green'}
              label="Primary leader set"
              value={`${formatNumber(stats.withPrimary)} of ${formatNumber(active.length)}`}
              hint={stats.withPrimary < active.length ? `${formatNumber(active.length - stats.withPrimary)} need attention` : 'Every active campus'}
              emphasis={stats.withPrimary < active.length}
              onClick={perms.canViewLeaders ? () => navigate('/app/locations/leaders') : undefined}
            />
          </SimpleGrid>

          <Grid>
            <Grid.Col span={{ base: 12, lg: 8 }}>
              {rows.length === 0 && !views.error ? (
                <Card withBorder radius="md" p="xl">
                  <EmptyState
                    icon={<IconBuildingChurch size={48} />}
                    title="No locations yet"
                    description="Add your first campus. Other modules pick it up straight away."
                    actionLabel={perms.canCreate ? 'New location' : undefined}
                    onAction={perms.canCreate ? () => navigate(NEW_LOCATION_PATH) : undefined}
                  />
                </Card>
              ) : (
                <SimpleGrid cols={{ base: 1, sm: 2 }}>
                  {rows.map((view) => {
                    const ls = leaderState(view.leaders);
                    return (
                      <CampusCard
                        key={view.locationID}
                        view={view}
                        primaryName={ls.ok ? names.nameOf(ls.primaryIds[0]) : null}
                        onOpen={() => navigate(locationPath(view.locationID))}
                      />
                    );
                  })}
                </SimpleGrid>
              )}
            </Grid.Col>

            <Grid.Col span={{ base: 12, lg: 4 }}>
              <SectionCard
                icon={<IconAlertTriangle size={18} />}
                color={attention.length ? 'orange' : 'green'}
                title="Needs attention"
                description="Active campuses with gaps that show up in other modules"
              >
                {attention.length === 0 ? (
                  <Group gap="sm" wrap="nowrap">
                    <IconCircleCheck size={20} color="var(--mantine-color-green-6)" />
                    <Text size="sm">Every active campus has an address, a service time and one primary leader.</Text>
                  </Group>
                ) : (
                  <Stack gap={0}>
                    {attention.map(({ view, issue }) => (
                      <Group
                        key={`${view.locationID}-${issue.key}`}
                        justify="space-between"
                        wrap="nowrap"
                        py={8}
                        style={{ borderBottom: '1px solid var(--mantine-color-default-border)', cursor: 'pointer' }}
                        onClick={() => navigate(locationPath(view.locationID, issue.tab))}
                      >
                        <Group gap={8} wrap="nowrap" style={{ minWidth: 0 }}>
                          <ColorSwatch color={view.color} size={10} />
                          <Stack gap={0} style={{ minWidth: 0 }}>
                            <Text size="sm" fw={600} lineClamp={1}>
                              {view.name}
                            </Text>
                            <Text size="xs" c={issue.severity === 'high' ? 'red' : 'orange'}>
                              {issue.label}
                            </Text>
                          </Stack>
                        </Group>
                        <Tooltip label="Open" withArrow>
                          <IconChevronRight size={16} style={{ flexShrink: 0, color: 'var(--mantine-color-dimmed)' }} />
                        </Tooltip>
                      </Group>
                    ))}
                  </Stack>
                )}
                {inactiveCount > 0 && (
                  <Text size="xs" c="dimmed" mt="sm">
                    {plural(inactiveCount, 'inactive location')} not checked.
                  </Text>
                )}
              </SectionCard>
            </Grid.Col>
          </Grid>
        </Stack>
      </FadeIn>
    </LayoutContainer>
  );
}
